import { useCallback, useState } from 'react';
import api from '../../services/api';
import { waitForJob } from '../../utils/waitForJob';

const BLANK_TEST_TICKET = { key: 'TEST-001', summary: '', description: '', status: 'Open' };

/**
 * Server-side bulk estimation run plus the ad-hoc estimation tester. Both talk
 * to Jev through the API, so they share one hook.
 *
 * @param {(message: string, severity?: string) => void} notify snackbar reporter
 * @param {() => Promise<any>} fetchTickets context refresher, called after a successful run
 */
export default function useJevEstimation(notify, fetchTickets) {
  const [estimationLoading, setEstimationLoading] = useState(false);
  const [estimationStatus, setEstimationStatus] = useState('');

  const [testTicket, setTestTicket] = useState(BLANK_TEST_TICKET);
  const [testLoading, setTestLoading] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const start = useCallback(async () => {
    setEstimationLoading(true);
    setEstimationStatus('Starting estimation...');
    try {
      const started = await api.post('/jira/estimate-missing-effort');
      notify(started.data.message || 'Effort estimation started.', 'info');

      const job = await waitForJob(api, started.data.jobId, {
        onProgress: (progress) => {
          setEstimationStatus(
            `Estimating... ${progress.processed}/${progress.total} ` +
              `(estimated ${progress.estimated}, errors ${progress.errors})`
          );
        },
      });

      const result = job.result || {};
      const message = result.message || 'Effort estimation process finished.';
      notify(
        `${message} (Processed: ${result.totalAttempted || 0}, Estimated: ${
          result.estimated || 0
        }, Errors: ${result.errors || 0})`,
        result.errors > 0 ? 'warning' : 'success'
      );
      setEstimationStatus(message);
      if ((result.estimated || 0) > 0) {
        fetchTickets();
      }
    } catch (error) {
      console.error('Error triggering standalone effort estimation:', error);
      const errorMessage =
        error.response?.data?.message || error.message || 'Failed to trigger effort estimation.';
      notify(`Estimation failed: ${errorMessage}`, 'error');
      setEstimationStatus('Estimation failed.');
    } finally {
      setEstimationLoading(false);
    }
  }, [fetchTickets, notify]);

  // Loading stays on deliberately: the run continues server-side until it halts.
  const stop = useCallback(async () => {
    setEstimationLoading(true);
    setEstimationStatus('Attempting to stop estimation...');
    try {
      const response = await api.post('/jira/stop-effort-estimation');
      notify(
        response.data.message || 'Stop signal sent.',
        response.data.success ? 'success' : 'error'
      );
      setEstimationStatus('Stop signal sent. Process may take a moment to halt.');
    } catch (error) {
      console.error('Error stopping effort estimation:', error);
      const errorMessage =
        error.response?.data?.message || error.message || 'Failed to send stop signal.';
      notify(`Failed to stop: ${errorMessage}`, 'error');
      setEstimationStatus('Failed to send stop signal.');
    }
  }, [notify]);

  const runTest = useCallback(async () => {
    if (!testTicket.summary) {
      notify('Please provide at least a ticket summary', 'warning');
      return;
    }

    setTestLoading(true);
    setTestResult(null);
    try {
    const response = await api.post('/jev/estimate', {
      ticket: testTicket,
      withReasoning: true,
    });
      setTestResult(response.data);
      notify('Estimation test completed successfully', 'success');
    } catch (error) {
      console.error('Error testing estimation:', error);
      notify(
        `Test failed: ${error.response?.data?.message || error.message || 'Unknown error'}`,
        'error'
      );
      setTestResult({ error: error.response?.data?.error || error.message, hours: null });
    } finally {
      setTestLoading(false);
    }
  }, [testTicket, notify]);

  const clearTest = () => {
    setTestTicket(BLANK_TEST_TICKET);
    setTestResult(null);
  };

  return {
    run: { onStart: start, onStop: stop, loading: estimationLoading, status: estimationStatus },
    tester: {
      testTicket,
      setTestTicket,
      onTest: runTest,
      onClear: clearTest,
      loading: testLoading,
      result: testResult,
    },
  };
}
